import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatNum, apiErr } from "../lib/utils";
import { Badge } from "./ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, Legend, Cell } from "recharts";
import { Droplets, Zap, TrendingUp, Receipt, AlertTriangle, Gauge } from "lucide-react";
import { toast } from "sonner";

const WATER = "#38BDF8";
const ELEC = "#FFB800";
const slug = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");

function Stat({ icon: Icon, label, value, unit, tone, testid }) {
  return (
    <div className="card-flat p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground mb-2"><Icon className="h-4 w-4" /><span className="label-eyebrow text-[10px]">{label}</span></div>
      <div className={`font-mono text-2xl font-bold tracking-tight ${tone || ""}`}>{value}<span className="text-xs text-muted-foreground font-sans ml-1">{unit}</span></div>
    </div>
  );
}

export function CostTrendsTab() {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("cost"); // "cost" | "consumption"

  const load = (y) => {
    setLoading(true);
    const params = y ? { year: y } : {};
    api.get("/esg-records/cost-trends", { params })
      .then((r) => { setData(r.data); setYear(r.data.year); })
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(null); }, []);

  if (loading && !data) return <div className="p-6 text-muted-foreground font-mono text-sm" data-testid="cost-trends-loading">Loading bill cost trends…</div>;
  if (!data) return null;

  const cur = data.currency || "SGD";
  const money = (v) => `${cur} ${formatNum(v || 0, { decimals: 0 })}`;
  const vol = (v) => formatNum(v || 0, { decimals: 0 });
  const hasConsumption = ((data.total_water_m3 || 0) + (data.total_electricity_kwh || 0)) > 0;
  const consMonthly = data.consumption_monthly || [];
  const consJumps = data.consumption_jumps || [];

  const YearPicker = () => (
    (data.years || []).length > 1 && (
      <Select value={String(year)} onValueChange={(v) => { setYear(v); load(v); }}>
        <SelectTrigger className="w-28 h-9" data-testid="cost-year-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          {data.years.map((y) => <SelectItem key={y} value={y} data-testid={`cost-year-${y}`}>FY{y}</SelectItem>)}
        </SelectContent>
      </Select>
    )
  );

  const ViewToggle = () => (
    (data.has_cost && hasConsumption) ? (
      <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid="ctab-view-toggle">
        <button type="button" onClick={() => setView("cost")} data-testid="ctab-view-cost"
          className={`px-3 h-9 text-xs font-medium transition-colors ${view === "cost" ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-muted/40"}`}>
          <Receipt className="h-3.5 w-3.5 inline mr-1" />Cost
        </button>
        <button type="button" onClick={() => setView("consumption")} data-testid="ctab-view-consumption"
          className={`px-3 h-9 text-xs font-medium transition-colors ${view === "consumption" ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-muted/40"}`}>
          <Gauge className="h-3.5 w-3.5 inline mr-1" />Consumption
        </button>
      </div>
    ) : null
  );

  // Nothing captured at all.
  if (!data.has_cost && !hasConsumption) {
    return (
      <div className="space-y-4" data-testid="cost-trends-tab">
        <div className="flex items-center justify-between gap-3">
          <div><div className="font-display font-semibold">Bill Cost & Consumption Trends</div>
            <div className="text-xs text-muted-foreground">Monthly water & electricity spend and usage per site, flagged when a bill or reading jumps.</div></div>
          <YearPicker />
        </div>
        <div className="card-flat p-10 text-center text-muted-foreground" data-testid="cost-trends-empty">
          <Receipt className="h-8 w-8 mx-auto mb-3 opacity-40" />
          No bill costs or utility readings captured yet. Costs and consumption are read from utility bills you
          upload via the Document Inbox (water cost = all water charges excl GST). Upload a bill and it will appear here.
        </div>
      </div>
    );
  }

  const showConsumption = view === "consumption" || !data.has_cost;

  return (
    <div className="space-y-4" data-testid="cost-trends-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display font-semibold">{showConsumption ? "Consumption Trends" : "Bill Cost Trends"} · FY{year}</div>
          <div className="text-xs text-muted-foreground">
            {showConsumption
              ? "Monthly water (m³) & electricity (kWh) usage per site. A month is flagged when it exceeds 1.4× its trailing average."
              : `Monthly water & electricity spend per site (${cur}, excl GST). A month is flagged when it exceeds 1.4× its trailing average.`}
          </div>
        </div>
        <div className="flex items-center gap-2"><ViewToggle /><YearPicker /></div>
      </div>

      {showConsumption ? (
        <div className="space-y-4" data-testid="consumption-view">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Stat icon={Droplets} label="Water used (yr)" value={vol(data.total_water_m3)} unit="m³" tone="text-[#38BDF8]" testid="cons-stat-water" />
            <Stat icon={Zap} label="Electricity used (yr)" value={vol(data.total_electricity_kwh)} unit="kWh" tone="text-[#FFB800]" testid="cons-stat-electricity" />
            <Stat icon={TrendingUp} label="Flagged spikes" value={consJumps.length} tone={consJumps.length ? "text-red-500" : "text-[#00E65B]"} testid="cons-stat-jumps" />
          </div>

          <div className="card-flat p-4" data-testid="consumption-chart">
            <div className="label-eyebrow mb-2">Monthly usage — all sites</div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={consMonthly} margin={{ left: 8, right: 16, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#888" }} />
                <YAxis yAxisId="w" tick={{ fontSize: 10, fill: "#38BDF8" }} />
                <YAxis yAxisId="e" orientation="right" tick={{ fontSize: 10, fill: "#FFB800" }} />
                <RTooltip contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 12 }}
                  formatter={(v, n) => [n === "water_m3" ? `${formatNum(v, { decimals: 0 })} m³` : `${formatNum(v, { decimals: 0 })} kWh`, n === "water_m3" ? "Water" : "Electricity"]} />
                <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => (v === "water_m3" ? "Water (m³)" : "Electricity (kWh)")} />
                <Bar yAxisId="w" dataKey="water_m3" fill={WATER} radius={[3, 3, 0, 0]} maxBarSize={22}>
                  {consMonthly.map((m, i) => <Cell key={i} fill={m.water_jump ? "#EF4444" : WATER} />)}
                </Bar>
                <Bar yAxisId="e" dataKey="electricity_kwh" fill={ELEC} radius={[3, 3, 0, 0]} maxBarSize={22}>
                  {consMonthly.map((m, i) => <Cell key={i} fill={m.electricity_jump ? "#EF4444" : ELEC} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="text-[10px] text-muted-foreground mt-1">Red bars mark a month flagged as an unusual usage spike vs its trailing average.</div>
          </div>

          <div className="card-flat p-4" data-testid="consumption-jumps-panel">
            <div className="label-eyebrow mb-2 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-red-500" /> Flagged usage spikes</div>
            {consJumps.length === 0
              ? <div className="text-xs text-muted-foreground" data-testid="consumption-jumps-empty">No unusual usage spikes this year — every month is within 1.4× its trailing average.</div>
              : <div className="space-y-1.5">
                {consJumps.map((j, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 text-xs border-b border-border/50 pb-1.5" data-testid={`consumption-jump-${i}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      {j.kind === "water" ? <Droplets className="h-3.5 w-3.5 text-[#38BDF8] shrink-0" /> : <Zap className="h-3.5 w-3.5 text-[#FFB800] shrink-0" />}
                      <span className="truncate font-medium">{j.facility}</span>
                      <span className="text-muted-foreground">· {j.label} FY{year}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="font-mono">{vol(j.value)} {j.unit}</span>
                      <Badge variant="warning" className="text-[9px]">+{j.pct}% vs {vol(j.baseline)} {j.unit}</Badge>
                    </div>
                  </div>
                ))}
              </div>}
          </div>

          <div className="card-flat p-4" data-testid="consumption-facility-breakdown">
            <div className="label-eyebrow mb-2">Usage by site</div>
            <div className="space-y-1">
              {data.by_facility.map((f) => (
                <div key={f.facility} className="flex items-center justify-between gap-3 text-xs py-1.5 border-b border-border/40" data-testid={`consumption-facility-row-${slug(f.facility)}`}>
                  <span className="truncate font-medium min-w-0">{f.facility}</span>
                  <div className="flex items-center gap-4 shrink-0 font-mono">
                    <span className="text-[#38BDF8]"><Droplets className="h-3 w-3 inline mr-1" />{vol(f.water_m3_total)} m³</span>
                    <span className="text-[#FFB800]"><Zap className="h-3 w-3 inline mr-1" />{vol(f.electricity_kwh_total)} kWh</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="cost-view">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Stat icon={Droplets} label="Water spend (yr)" value={money(data.total_water)} tone="text-[#38BDF8]" testid="cost-stat-water" />
            <Stat icon={Zap} label="Electricity spend (yr)" value={money(data.total_electricity)} tone="text-[#FFB800]" testid="cost-stat-electricity" />
            <Stat icon={TrendingUp} label="Flagged jumps" value={(data.jumps || []).length} tone={(data.jumps || []).length ? "text-red-500" : "text-[#00E65B]"} testid="cost-stat-jumps" />
          </div>

          <div className="card-flat p-4" data-testid="cost-trends-chart">
            <div className="label-eyebrow mb-2">Monthly spend — all sites ({cur})</div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.monthly} margin={{ left: 8, right: 16, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#888" }} />
                <YAxis tick={{ fontSize: 10, fill: "#888" }} />
                <RTooltip contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 12 }}
                  formatter={(v, n) => [`${cur} ${formatNum(v, { decimals: 0 })}`, n === "water" ? "Water" : "Electricity"]} />
                <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => (v === "water" ? "Water" : "Electricity")} />
                <Bar dataKey="water" fill={WATER} radius={[3, 3, 0, 0]} maxBarSize={22}>
                  {data.monthly.map((m, i) => <Cell key={i} fill={m.water_jump ? "#EF4444" : WATER} />)}
                </Bar>
                <Bar dataKey="electricity" fill={ELEC} radius={[3, 3, 0, 0]} maxBarSize={22}>
                  {data.monthly.map((m, i) => <Cell key={i} fill={m.electricity_jump ? "#EF4444" : ELEC} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="text-[10px] text-muted-foreground mt-1">Red bars mark a month flagged as an unusual jump vs its trailing average.</div>
          </div>

          <div className="card-flat p-4" data-testid="cost-jumps-panel">
            <div className="label-eyebrow mb-2 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-red-500" /> Flagged cost jumps</div>
            {(data.jumps || []).length === 0
              ? <div className="text-xs text-muted-foreground" data-testid="cost-jumps-empty">No unusual bill jumps this year — every month is within 1.4× its trailing average.</div>
              : <div className="space-y-1.5">
                {data.jumps.map((j, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 text-xs border-b border-border/50 pb-1.5" data-testid={`cost-jump-${i}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      {j.kind === "water" ? <Droplets className="h-3.5 w-3.5 text-[#38BDF8] shrink-0" /> : <Zap className="h-3.5 w-3.5 text-[#FFB800] shrink-0" />}
                      <span className="truncate font-medium">{j.facility}</span>
                      <span className="text-muted-foreground">· {j.label} FY{year}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="font-mono">{money(j.cost)}</span>
                      <Badge variant="warning" className="text-[9px]">+{j.pct}% vs {money(j.baseline)}</Badge>
                    </div>
                  </div>
                ))}
              </div>}
          </div>

          {(data.by_group || []).length > 0 && (
            <div className="card-flat p-4" data-testid="cost-group-breakdown">
              <div className="label-eyebrow mb-2">Spend by group ({cur})</div>
              <div className="space-y-1">
                {data.by_group.map((g) => (
                  <div key={g.group} className="flex items-center justify-between gap-3 text-xs py-1.5 border-b border-border/40" data-testid={`cost-group-row-${slug(g.group)}`}>
                    <span className="truncate font-medium min-w-0">{g.group} <span className="text-muted-foreground font-normal">· {g.sites} site{g.sites > 1 ? "s" : ""}</span></span>
                    <div className="flex items-center gap-4 shrink-0 font-mono">
                      <span className="text-[#38BDF8]"><Droplets className="h-3 w-3 inline mr-1" />{money(g.water_total)}</span>
                      <span className="text-[#FFB800]"><Zap className="h-3 w-3 inline mr-1" />{money(g.electricity_total)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="card-flat p-4" data-testid="cost-facility-breakdown">
            <div className="label-eyebrow mb-2">Spend by site ({cur})</div>
            <div className="space-y-1">
              {data.by_facility.map((f) => (
                <div key={f.facility} className="flex items-center justify-between gap-3 text-xs py-1.5 border-b border-border/40" data-testid={`cost-facility-row-${slug(f.facility)}`}>
                  <span className="truncate font-medium min-w-0">{f.facility}</span>
                  <div className="flex items-center gap-4 shrink-0 font-mono">
                    <span className="text-[#38BDF8]"><Droplets className="h-3 w-3 inline mr-1" />{money(f.water_total)}</span>
                    <span className="text-[#FFB800]"><Zap className="h-3 w-3 inline mr-1" />{money(f.electricity_total)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
